import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { reportService } from '../../services/reportService';
import {
  ReportFilters,
  ExecutiveDashboardData,
  TopDecliner,
  YearEndProjection,
  ItemMode,
} from '../../types/Report';
import FilterBar from '../../components/reports/FilterBar';
import ReportHeader from '../../components/reports/ReportHeader';
import ExportButton from '../../components/reports/ExportButton';
import {
  appendExecutiveDashboardSearchParams,
  ExecutiveDashboardView,
  parseExecutiveDashboardView,
} from '../../utils/reportFiltersComparison';
import ExecutiveDashboardMainTabs from '../../components/reports/ExecutiveDashboardMainTabs';
import ExecutiveComparisonBreakdownPanel from '../../components/reports/ExecutiveComparisonBreakdownPanel';
import ExecutiveMovementInsightsPanel from '../../components/reports/ExecutiveMovementInsightsPanel';
import { format_yoy_change_percent } from '../../utils/yoyDisplay';

const ExecutiveDashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dashboardData, setDashboardData] = useState<ExecutiveDashboardData | null>(null);
  const [drillDownData, setDrillDownData] = useState<{
    type: string;
    itemName: string;
    itemId: string | number;
    data: { [key: string]: TopDecliner[] };
  } | null>(null);
  const [drillDownLoading] = useState(false);

  // Initialize default filters (first day of current year to today, YOY comparison)
  const getDefaultFilters = (): ReportFilters => {
    const now = new Date();
    const endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const startDate = new Date(now.getFullYear(), 0, 1); // January 1st of current year
    return {
      startDate: startDate.toISOString().split('T')[0],
      endDate: endDate.toISOString().split('T')[0],
      compareMode: 'yoy',
    };
  };

  // Read filters from query params on mount
  const getFiltersFromParams = (): ReportFilters => {
    const defaultFilters = getDefaultFilters();
    const filters: ReportFilters = {
      startDate: searchParams.get('startDate') || defaultFilters.startDate,
      endDate: searchParams.get('endDate') || defaultFilters.endDate,
      compareMode:
        (searchParams.get('compareMode') as ReportFilters['compareMode']) ||
        defaultFilters.compareMode,
    };

    // Read filter IDs directly from query params (preferred method)
    const customerIdsParam = searchParams.get('customerIds');
    if (customerIdsParam) {
      filters.customerIds = customerIdsParam
        .split(',')
        .map((s) => s.trim())
        .filter((s) => s.length > 0);
    }
    const productIdsParam = searchParams.get('productIds');
    if (productIdsParam) {
      filters.productIds = productIdsParam
        .split(',')
        .map((s) => s.trim())
        .filter((s) => s.length > 0)
        .map((s) => parseInt(s, 10))
        .filter((n) => !isNaN(n));
    }
    const categoryIdsParam = searchParams.get('categoryIds');
    if (categoryIdsParam) {
      filters.categoryIds = categoryIdsParam
        .split(',')
        .map((s) => s.trim())
        .filter((s) => s.length > 0);
    }
    const brandIdsParam = searchParams.get('brandIds');
    if (brandIdsParam) {
      filters.brandIds = brandIdsParam
        .split(',')
        .map((s) => s.trim())
        .filter((s) => s.length > 0);
    }
    const territoryIdsParam = searchParams.get('territoryIds');
    if (territoryIdsParam) {
      filters.territoryIds = territoryIdsParam
        .split(',')
        .map((s) => s.trim())
        .filter((s) => s.length > 0);
    }
    const salesPersonIdsParam = searchParams.get('salesPersonIds');
    if (salesPersonIdsParam) {
      filters.salesPersonIds = salesPersonIdsParam
        .split(',')
        .map((s) => s.trim())
        .filter((s) => s.length > 0);
    }

    const primaryDimensionParam = searchParams.get('primary_dimension');
    if (primaryDimensionParam) filters.primary_dimension = primaryDimensionParam.trim();
    const secondaryDimensionParam = searchParams.get('secondary_dimension');
    if (secondaryDimensionParam) filters.secondary_dimension = secondaryDimensionParam.trim();

    return filters;
  };

  const [filters, setFilters] = useState<ReportFilters>(getFiltersFromParams());
  const filtersRef = React.useRef(filters);
  filtersRef.current = filters;
  const [mainTab, setMainTab] = useState<ExecutiveDashboardView>(() =>
    parseExecutiveDashboardView(searchParams.get('dashboardView'))
  );
  const mainTabRef = React.useRef(mainTab);
  mainTabRef.current = mainTab;
  const [yearEndProjection, setYearEndProjection] = useState<YearEndProjection | null>(null);
  const [reportHasBeenRun, setReportHasBeenRun] = useState(
    () => searchParams.toString().length > 0
  );
  const [sortBy, setSortBy] = useState<{
    category?: 'percent' | 'absolute';
    brand?: 'percent' | 'absolute';
    product?: 'percent' | 'absolute';
    customer?: 'percent' | 'absolute';
    salesAgent?: 'percent' | 'absolute';
    territory?: 'percent' | 'absolute';
  }>({
    category: 'percent',
    brand: 'percent',
    product: 'percent',
    customer: 'percent',
    salesAgent: 'percent',
    territory: 'percent',
  });

  const [mode, setMode] = useState<{
    category?: ItemMode;
    brand?: ItemMode;
    product?: ItemMode;
    customer?: ItemMode;
    salesAgent?: ItemMode;
    territory?: ItemMode;
  }>({
    category: 'decliners',
    brand: 'decliners',
    product: 'decliners',
    customer: 'decliners',
    salesAgent: 'decliners',
    territory: 'decliners',
  });

  const [pageSize] = useState(10); // Items per page
  const [loadedCount, setLoadedCount] = useState<{
    category?: number;
    brand?: number;
    product?: number;
    customer?: number;
    salesAgent?: number;
    territory?: number;
  }>({
    category: 10, // Initial load for category
    brand: 10, // Initial load for brand
    product: 10,
    customer: 10,
    salesAgent: 10,
    territory: 10,
  });
  // Ref to access current loadedCount without triggering dependencies
  const loadedCountRef = React.useRef(loadedCount);
  React.useEffect(() => {
    loadedCountRef.current = loadedCount;
  }, [loadedCount]);

  // Detect sidebar width for loader positioning
  const [sidebarOffset, setSidebarOffset] = useState(250);
  useEffect(() => {
    const updateSidebarOffset = () => {
      const mainContent = document.querySelector('.main-content');
      if (mainContent) {
        const computedStyle = window.getComputedStyle(mainContent);
        const marginLeft = parseInt(computedStyle.marginLeft, 10) || 250;
        setSidebarOffset(marginLeft);
      }
    };
    updateSidebarOffset();
    // Update on window resize and sidebar toggle
    window.addEventListener('resize', updateSidebarOffset);
    const observer = new MutationObserver(updateSidebarOffset);
    const mainContent = document.querySelector('.main-content');
    if (mainContent) {
      observer.observe(mainContent, { attributes: true, attributeFilter: ['class'] });
    }
    return () => {
      window.removeEventListener('resize', updateSidebarOffset);
      observer.disconnect();
    };
  }, []);

  const loadDashboardData = useCallback(
    async (limits?: typeof loadedCount, skipMovers?: boolean) => {
      setLoading(true);
      setError(null);
      try {
        // Generate correlation ID for this dashboard load
        const correlationId = `dashboard-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

        // Use provided limits or current loadedCount from ref
        // loadedCount is removed from dependencies to prevent circular dependency:
        // handleLoadMore updates loadedCount -> recreates loadDashboardData -> triggers useEffect
        // By removing it, handleLoadMore can update loadedCount without triggering full reload
        const omitMovers = skipMovers ?? mainTabRef.current === 'breakdown';
        const currentLimits = omitMovers
          ? {
              category: 0,
              brand: 0,
              product: 0,
              customer: 0,
              salesAgent: 0,
              territory: 0,
            }
          : limits || loadedCountRef.current;
        const limit: {
          category?: number;
          brand?: number;
          product?: number;
          customer?: number;
          salesAgent?: number;
          territory?: number;
        } = {
          category: currentLimits.category,
          brand: currentLimits.brand,
          product: currentLimits.product,
          customer: currentLimits.customer,
          salesAgent: currentLimits.salesAgent,
          territory: currentLimits.territory,
        };

        const data = await reportService.getExecutiveDashboard(
          filters,
          sortBy,
          mode,
          limit,
          correlationId
        );
        setDashboardData(data);
      } catch (err: any) {
        setError(err.message || 'Failed to load dashboard data');
        console.error('Error loading dashboard data:', err);
      } finally {
        setLoading(false);
      }
    },
    [filters, sortBy, mode]
  );

  // Sync URL → state when search params change (avoid redundant setFilters → duplicate API)
  useEffect(() => {
    const paramsFilters = getFiltersFromParams();
    if (JSON.stringify(paramsFilters) !== JSON.stringify(filtersRef.current)) {
      setFilters(paramsFilters);
    }
    setMainTab(parseExecutiveDashboardView(searchParams.get('dashboardView')));
    if (searchParams.toString().length > 0) {
      setReportHasBeenRun(true);
    }
  }, [searchParams]);

  // Reload projection when dashboardData or filters change
  useEffect(() => {
    if (dashboardData) {
      loadYearEndProjection();
    }
  }, [dashboardData, filters]);

  const loadInitialData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Generate correlation ID for initial load
      const correlationId = `dashboard-init-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

      // Load only KPIs on initial load by setting all limits to 0
      // Backend skips top decliners when limit is 0
      const data = await reportService.getExecutiveDashboard(
        filters,
        sortBy,
        mode,
        {
          category: 0,
          brand: 0,
          product: 0,
          customer: 0,
          salesAgent: 0,
          territory: 0,
        },
        correlationId
      );
      // Set dashboard data with empty top decliners
      setDashboardData({
        ...data,
        topDecliners: {
          categories: [],
          brands: [],
          products: [],
          customers: [],
          salesAgents: [],
          territories: [],
        },
      });
    } catch (err: any) {
      setError(err.message || 'Failed to load dashboard data');
      console.error('Error loading dashboard data:', err);
    } finally {
      setLoading(false);
    }
  }, [filters, sortBy, mode]);

  useEffect(() => {
    if (!reportHasBeenRun) {
      void loadInitialData();
    } else {
      void loadDashboardData();
    }
  }, [reportHasBeenRun, loadInitialData, loadDashboardData]);

  // Reset pagination when filters, sortBy, or mode changes
  useEffect(() => {
    setLoadedCount({
      category: 10,
      brand: 10,
      product: 10,
      customer: 10,
      salesAgent: 10,
      territory: 10,
    });
  }, [filters, sortBy, mode]);

  // Handle mode changes - only reload the affected dimension
  const handleModeChange = useCallback(
    async (
      typeKey: 'category' | 'brand' | 'product' | 'customer' | 'salesAgent' | 'territory',
      newMode: ItemMode
    ) => {
      if (!dashboardData) return;

      setMode((prev) => ({ ...prev, [typeKey]: newMode }));

      // Only reload the affected dimension
      try {
        const currentSortBy = sortBy[typeKey] || 'absolute';
        const currentLimit = loadedCount[typeKey] || 10;

        const data = await reportService.getDimensionData(
          typeKey,
          filters,
          currentSortBy,
          newMode,
          currentLimit
        );

        // Update only the affected dimension in dashboardData
        setDashboardData((prev) => {
          if (!prev) return prev;
          const keyMap: { [key: string]: keyof typeof prev.topDecliners } = {
            category: 'categories',
            brand: 'brands',
            product: 'products',
            customer: 'customers',
            salesAgent: 'salesAgents',
            territory: 'territories',
          };
          return {
            ...prev,
            topDecliners: {
              ...prev.topDecliners,
              [keyMap[typeKey]]: data,
            },
          };
        });
      } catch (err: any) {
        console.error(`Error loading ${typeKey} data:`, err);
        setError(err.message || `Failed to load ${typeKey} data`);
      }
    },
    [dashboardData, filters, sortBy, loadedCount]
  );

  // Handle sortBy change - only reload the affected dimension
  const handleSortByChange = useCallback(
    async (
      typeKey: 'category' | 'brand' | 'product' | 'customer' | 'salesAgent' | 'territory',
      newSortBy: 'percent' | 'absolute'
    ) => {
      if (!dashboardData) return;

      setSortBy((prev) => ({
        ...prev,
        [typeKey]: newSortBy,
      }));

      // Only reload the affected dimension
      try {
        const currentMode = mode[typeKey] || 'decliners';
        const currentLimit = loadedCount[typeKey] || 10;

        const data = await reportService.getDimensionData(
          typeKey,
          filters,
          newSortBy,
          currentMode,
          currentLimit
        );

        // Update only the affected dimension in dashboardData
        setDashboardData((prev) => {
          if (!prev) return prev;
          const keyMap: { [key: string]: keyof typeof prev.topDecliners } = {
            category: 'categories',
            brand: 'brands',
            product: 'products',
            customer: 'customers',
            salesAgent: 'salesAgents',
            territory: 'territories',
          };
          return {
            ...prev,
            topDecliners: {
              ...prev.topDecliners,
              [keyMap[typeKey]]: data,
            },
          };
        });
      } catch (err: any) {
        console.error(`Error loading ${typeKey} data:`, err);
        setError(err.message || `Failed to load ${typeKey} data`);
      }
    },
    [dashboardData, filters, mode, loadedCount]
  );

  // Handle load more - only reload the affected dimension
  const handleLoadMore = useCallback(
    async (typeKey: 'category' | 'brand' | 'product' | 'customer' | 'salesAgent' | 'territory') => {
      if (!dashboardData) return;

      const newLimit = (loadedCount[typeKey] || 10) + pageSize;
      setLoadedCount((prev) => ({
        ...prev,
        [typeKey]: newLimit,
      }));

      // Only reload the affected dimension
      try {
        const currentSortBy = sortBy[typeKey] || 'absolute';
        const currentMode = mode[typeKey] || 'decliners';

        const data = await reportService.getDimensionData(
          typeKey,
          filters,
          currentSortBy,
          currentMode,
          newLimit
        );

        // Update only the affected dimension in dashboardData
        setDashboardData((prev) => {
          if (!prev) return prev;
          const keyMap: { [key: string]: keyof typeof prev.topDecliners } = {
            category: 'categories',
            brand: 'brands',
            product: 'products',
            customer: 'customers',
            salesAgent: 'salesAgents',
            territory: 'territories',
          };
          return {
            ...prev,
            topDecliners: {
              ...prev.topDecliners,
              [keyMap[typeKey]]: data,
            },
          };
        });
      } catch (err: any) {
        console.error(`Error loading more ${typeKey} data:`, err);
        setError(err.message || `Failed to load more ${typeKey} data`);
        // Revert the loadedCount on error
        setLoadedCount((prev) => ({
          ...prev,
          [typeKey]: prev[typeKey] || 10,
        }));
      }
    },
    [dashboardData, filters, sortBy, mode, loadedCount, pageSize]
  );

  const loadYearEndProjection = async () => {
    try {
      const currentYear = new Date().getFullYear();
      // Only pass KPIs when there is a positive prior; otherwise the API runs SQL
      // for YTDs (KPI can show prior=0 while we still need DB-consistent YTDs).
      const kpi = dashboardData?.kpis.revenue;
      const use_kpi_tails = (kpi?.prior ?? 0) > 0;
      const currentRevenue = use_kpi_tails ? kpi?.current : undefined;
      const priorRevenue = use_kpi_tails ? kpi?.prior : undefined;
      const projection = await reportService.getYearEndProjection(
        currentYear,
        currentRevenue,
        priorRevenue,
        filters
      );
      setYearEndProjection(projection);
    } catch (err: any) {
      console.error('Error loading year-end projection:', err);
      // Don't show error to user, just log it
    }
  };

  const handleFiltersChange = useCallback((newFilters: ReportFilters) => {
    setFilters(newFilters);
  }, []);

  const handleMainTabChange = useCallback(
    (tab: ExecutiveDashboardView) => {
      setMainTab(tab);
      navigate(
        `/reports/executive-dashboard?${appendExecutiveDashboardSearchParams(filters, tab).toString()}`
      );
      if (tab === 'movers' && reportHasBeenRun) {
        void loadDashboardData(undefined, false);
      }
    },
    [filters, navigate, reportHasBeenRun, loadDashboardData]
  );

  const handleRun = useCallback(() => {
    if (reportHasBeenRun) {
      void loadDashboardData();
    } else {
      setReportHasBeenRun(true);
    }
  }, [reportHasBeenRun, loadDashboardData]);

  const handleReset = () => {
    setFilters(getDefaultFilters());
    setReportHasBeenRun(true);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(num);
  };

  const formatPercentage = (value: number) => {
    const sign = value >= 0 ? '+' : '';
    return `${sign}${value.toFixed(1)}%`;
  };

  const KPITile: React.FC<{
    title: string;
    data: { current: number; prior: number; change: number; changePercent: number };
    formatter: (value: number) => string;
    onExport?: () => void;
  }> = ({ title, data, formatter, onExport }) => {
    const isPositive = data.change >= 0;
    const changeColor = isPositive ? '#28a745' : '#dc3545';

    return (
      <div
        style={{
          backgroundColor: 'var(--report-surface)',
          border: '1px solid var(--report-border)',
          borderRadius: '8px',
          padding: '1.5rem',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            marginBottom: '0.5rem',
          }}
        >
          <div style={{ fontSize: '0.9rem', color: 'var(--report-text-muted)' }}>{title}</div>
          {onExport && (
            <ExportButton
              onClick={onExport}
              size="small"
              variant="primary"
              title={`Export ${title} details`}
            />
          )}
        </div>
        <div
          style={{
            fontSize: '2rem',
            fontWeight: '600',
            marginBottom: '0.5rem',
            color: 'var(--report-text)',
          }}
        >
          {formatter(data.current)}
        </div>
        {filters.compareMode && (
          <div style={{ fontSize: '0.85rem', color: 'var(--report-text-muted)' }}>
            <div>
              Prior: {formatter(data.prior)} (
              {format_yoy_change_percent(data.prior, data.changePercent, formatPercentage)})
            </div>
            <div style={{ color: changeColor, fontWeight: '500', marginTop: '0.25rem' }}>
              {isPositive ? '↑' : '↓'} {formatter(Math.abs(data.change))}
            </div>
          </div>
        )}
      </div>
    );
  };

  const handleDrillDown = (
    type: 'customer' | 'product' | 'salesAgent' | 'territory' | 'category' | 'brand',
    itemId: string | number,
    _itemName: string
  ) => {
    // Navigate to URL-based route with date and compare mode query params
    const urlType = type === 'salesAgent' ? 'salesagent' : type.toLowerCase();
    // Trim product codes to remove trailing spaces
    const urlItemId = type === 'product' && typeof itemId === 'string' ? itemId.trim() : itemId;
    const params = appendExecutiveDashboardSearchParams(filters, mainTab);
    navigate(`/reports/executive-dashboard/${urlType}/${urlItemId}?${params.toString()}`);
  };

  const handleBackToMain = () => {
    setDrillDownData(null);
  };

  const handleExport = async (
    type: 'category' | 'brand' | 'product' | 'customer' | 'salesAgent' | 'territory'
  ) => {
    setLoading(true);
    try {
      setError(null);
      const currentSortBy = sortBy[type] || 'absolute';
      const currentMode = mode[type] || 'decliners';
      const blob = await reportService.exportTopDeclinersToExcel(
        type,
        filters,
        drillDownData?.type as
          | 'customer'
          | 'product'
          | 'salesAgent'
          | 'territory'
          | 'category'
          | 'brand'
          | undefined,
        drillDownData?.itemId,
        currentSortBy,
        currentMode
      );

      // Generate filename
      const dimensionName = type.charAt(0).toUpperCase() + type.slice(1);
      const contextPart = drillDownData?.itemName
        ? `_${drillDownData.itemName.replace(/[^a-zA-Z0-9]/g, '_')}`
        : '';
      const dateRange =
        filters.startDate && filters.endDate
          ? `${filters.startDate}_to_${filters.endDate}`
          : new Date().toISOString().split('T')[0];
      const modeLabel = currentMode === 'performers' ? 'Performers' : 'Declining';
      const filename = `Top_${modeLabel}_${dimensionName}${contextPart}_${dateRange}.xlsx`;

      // Download file
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err: any) {
      setError(err.message || 'Failed to export Excel file');
      console.error('Error exporting Excel:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleExportKPI = async (metric: 'revenue' | 'orders', label: string) => {
    setLoading(true);
    try {
      setError(null);
      const blob = await reportService.exportExecutiveDashboardKPI(metric, filters);

      const sanitizedLabel = label.replace(/[^a-zA-Z0-9]/g, '_');
      const dateRange =
        filters.startDate && filters.endDate
          ? `${filters.startDate}_to_${filters.endDate}`
          : new Date().toISOString().split('T')[0];
      const filename = `executive-dashboard_${sanitizedLabel}_${dateRange}.xlsx`;

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err: any) {
      setError(err.message || 'Failed to export KPI data');
      console.error('Error exporting KPI data:', err);
    } finally {
      setLoading(false);
    }
  };

  const TopDeclinersWidget: React.FC<{
    title: string;
    decliners: TopDecliner[];
    limit: number;
    drillDownType?: 'customer' | 'product' | 'salesAgent' | 'territory' | 'category' | 'brand';
    exportType?: 'category' | 'brand' | 'product' | 'customer' | 'salesAgent' | 'territory';
    showRankingToggle?: boolean;
    sortBy?: 'percent' | 'absolute';
    onSortByChange?: (newSortBy: 'percent' | 'absolute') => void;
    mode?: ItemMode;
    onModeChange?: (newMode: ItemMode) => void;
    showViewMore?: boolean;
    onViewMore?: () => void;
    typeKey?: 'category' | 'brand' | 'product' | 'customer' | 'salesAgent' | 'territory';
    hasMore?: boolean;
  }> = ({
    title,
    decliners,
    drillDownType,
    exportType,
    showRankingToggle = false,
    sortBy,
    onSortByChange,
    mode = 'decliners',
    onModeChange,
    showViewMore = false,
    onViewMore,
    typeKey,
    hasMore = false,
  }) => {
    // No client-side sorting - data is already sorted by the backend
    const displayTitle = title.replace(
      'Decliners',
      mode === 'performers' ? 'Performers' : 'Decliners'
    );

    return (
      <div
        style={{
          backgroundColor: 'var(--report-surface)',
          border: '1px solid var(--report-border)',
          borderRadius: '8px',
          padding: '1.5rem',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            marginBottom: '1rem',
          }}
        >
          <h3
            style={{
              width: '100%',
              margin: 0,
              marginBottom: '1rem',
              fontSize: '1.1rem',
              fontWeight: '600',
            }}
          >
            {displayTitle}
          </h3>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
            {onModeChange && (
              <div
                style={{
                  display: 'flex',
                  gap: '0.25rem',
                  backgroundColor: 'var(--report-surface-subtle)',
                  borderRadius: '4px',
                  padding: '0.125rem',
                }}
              >
                <button
                  onClick={() => onModeChange('decliners')}
                  style={{
                    height: '32px',
                    padding: '0.25rem 0.5rem',
                    backgroundColor: mode === 'decliners' ? '#dc3545' : 'transparent',
                    color: mode === 'decliners' ? 'white' : 'var(--report-text-muted)',
                    border: 'none',
                    borderRadius: '3px',
                    cursor: 'pointer',
                    fontSize: '0.75rem',
                    fontWeight: mode === 'decliners' ? '600' : '400',
                  }}
                  title="Show decliners"
                >
                  Decliners
                </button>
                <button
                  onClick={() => onModeChange('performers')}
                  style={{
                    height: '32px',
                    padding: '0.25rem 0.5rem',
                    backgroundColor: mode === 'performers' ? '#28a745' : 'transparent',
                    color: mode === 'performers' ? 'white' : 'var(--report-text-muted)',
                    border: 'none',
                    borderRadius: '3px',
                    cursor: 'pointer',
                    fontSize: '0.75rem',
                    fontWeight: mode === 'performers' ? '600' : '400',
                  }}
                  title="Show performers"
                >
                  Performers
                </button>
              </div>
            )}
            {showRankingToggle && (
              <div
                style={{
                  display: 'flex',
                  gap: '0.25rem',
                  backgroundColor: 'var(--report-surface-subtle)',
                  borderRadius: '4px',
                  padding: '0.125rem',
                }}
              >
                <button
                  onClick={() => onSortByChange?.('percent')}
                  style={{
                    height: '32px',
                    padding: '0.25rem 0.5rem',
                    backgroundColor: sortBy === 'percent' ? 'var(--report-accent)' : 'transparent',
                    color: sortBy === 'percent' ? 'white' : 'var(--report-text-muted)',
                    border: 'none',
                    borderRadius: '3px',
                    cursor: 'pointer',
                    fontSize: '0.75rem',
                    fontWeight: sortBy === 'percent' ? '600' : '400',
                  }}
                  title="Sort by percentage"
                >
                  SortBy %
                </button>
                <button
                  onClick={() => onSortByChange?.('absolute')}
                  style={{
                    height: '32px',
                    padding: '0.25rem 0.5rem',
                    backgroundColor: sortBy === 'absolute' ? 'var(--report-accent)' : 'transparent',
                    color: sortBy === 'absolute' ? 'white' : 'var(--report-text-muted)',
                    border: 'none',
                    borderRadius: '3px',
                    cursor: 'pointer',
                    fontSize: '0.75rem',
                    fontWeight: sortBy === 'absolute' ? '600' : '400',
                  }}
                  title="Sort by absolute value"
                >
                  SortBy $
                </button>
              </div>
            )}
            {exportType && (
              <ExportButton
                onClick={() => handleExport(exportType)}
                size="medium"
                variant="primary"
                title="Download Excel"
              />
            )}
          </div>
        </div>
        {decliners.length === 0 ? (
          <div
            style={{
              color: 'var(--report-text-muted)',
              fontStyle: 'italic',
              padding: '1rem',
              textAlign: 'center',
            }}
          >
            No {mode === 'performers' ? 'performers' : 'decliners'} found. This could mean:
            <ul style={{ textAlign: 'left', marginTop: '0.5rem', paddingLeft: '1.5rem' }}>
              <li>
                All items are {mode === 'performers' ? 'declining or stable' : 'growing or stable'}
              </li>
              <li>No data available for the selected period</li>
              <li>Category/Brand/Product data may not be populated</li>
            </ul>
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {decliners.map((decliner, index) => (
                <div
                  key={
                    decliner.itemId !== undefined
                      ? `${decliner.itemId}-${index}`
                      : `decliner-${index}`
                  }
                  onClick={() => {
                    if (drillDownType && decliner.itemId !== undefined) {
                      handleDrillDown(drillDownType, decliner.itemId, decliner.item);
                    }
                  }}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '0.5rem',
                    backgroundColor: 'var(--report-surface-subtle)',
                    borderRadius: '4px',
                    cursor: drillDownType ? 'pointer' : 'default',
                    transition: 'all 0.2s',
                  }}
                  onMouseEnter={(e) => {
                    if (drillDownType) {
                      e.currentTarget.style.backgroundColor = 'var(--report-row-hover)';
                      e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.15)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (drillDownType) {
                      e.currentTarget.style.backgroundColor = 'var(--report-surface-subtle)';
                      e.currentTarget.style.boxShadow = 'none';
                    }
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: '500' }}>
                      {decliner.item}
                      {typeKey === 'salesAgent' && decliner.itemId && (
                        <span
                          style={{
                            marginLeft: '0.5rem',
                            fontSize: '0.75rem',
                            color: 'var(--report-text-muted)',
                            fontWeight: '400',
                          }}
                        >
                          ({decliner.itemId})
                        </span>
                      )}
                      {drillDownType && (
                        <span
                          style={{
                            marginLeft: '0.5rem',
                            fontSize: '0.75rem',
                            color: 'var(--report-accent)',
                          }}
                        >
                          ▶
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: '0.85rem', color: 'var(--report-text-muted)' }}>
                      Current: {formatCurrency(decliner.current)} | Prior:{' '}
                      {formatCurrency(decliner.prior)}
                    </div>
                  </div>
                  <div
                    style={{
                      textAlign: 'right',
                      color: mode === 'performers' ? '#28a745' : '#dc3545',
                      fontWeight: '500',
                    }}
                  >
                    <div>{formatCurrency(decliner.change)}</div>
                    <div
                      style={{ fontSize: '0.85rem' }}
                      title={
                        decliner.prior <= 0
                          ? 'No prior-period sales in the comparison window—no year-over-year %'
                          : undefined
                      }
                    >
                      {format_yoy_change_percent(
                        decliner.prior,
                        decliner.changePercent,
                        formatPercentage
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {showViewMore && onViewMore && hasMore && (
              <button
                onClick={onViewMore}
                style={{
                  marginTop: '1rem',
                  padding: '0.5rem 1rem',
                  backgroundColor: 'var(--report-accent)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '0.9rem',
                  fontWeight: '500',
                  width: '100%',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#0056b3';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--report-accent)';
                }}
              >
                View More
              </button>
            )}
          </>
        )}
      </div>
    );
  };

  const YearEndProjectionWidget: React.FC<{ projection: YearEndProjection | null }> = ({
    projection,
  }) => {
    const [showDetails, setShowDetails] = useState(false);

    if (!projection) {
      return (
        <div
          style={{
            backgroundColor: 'var(--report-surface)',
            border: '1px solid var(--report-border)',
            borderRadius: '8px',
            padding: '1.5rem',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          }}
        >
          <div style={{ fontSize: '1.1rem', fontWeight: '600', marginBottom: '1rem' }}>
            Year-end Sales Projection
          </div>
          <div style={{ color: 'var(--report-text-muted)', fontStyle: 'italic' }}>
            Loading projection data...
          </div>
        </div>
      );
    }

    const isPositive = projection.growthPercent !== null && projection.growthPercent >= 0;
    const growthColor = isPositive ? '#28a745' : '#dc3545';
    const growthIndicator = isPositive ? '▲' : '▼';

    return (
      <div
        style={{
          backgroundColor: 'var(--report-surface)',
          border: '1px solid var(--report-border)',
          borderRadius: '8px',
          padding: '1.5rem',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '1rem',
          }}
        >
          <h3
            style={{
              margin: 0,
              fontSize: '1.1rem',
              fontWeight: '600',
              color: 'var(--report-text)',
            }}
          >
            Year-end Sales Projection
          </h3>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <button
              onClick={() => setShowDetails(!showDetails)}
              style={{
                padding: '0.25rem 0.5rem',
                backgroundColor: showDetails ? 'var(--report-text-muted)' : 'var(--report-accent)',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '0.85rem',
              }}
            >
              {showDetails ? 'Hide' : 'Details'}
            </button>
          </div>
        </div>

        {!projection.hasData ? (
          <div>
            <div style={{ color: '#dc3545', fontWeight: '500', marginBottom: '0.5rem' }}>N/A</div>
            {projection.warning && (
              <div
                style={{
                  fontSize: '0.85rem',
                  color: 'var(--report-text-muted)',
                  fontStyle: 'italic',
                }}
              >
                {projection.warning}
              </div>
            )}
          </div>
        ) : (
          <>
            {/* Single line layout: Current YTD | Last YTD | Growth % | Projected Full-Year */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr 1fr 1.5fr',
                gap: '1rem',
                padding: '1rem',
                backgroundColor: 'var(--report-surface-subtle)',
                borderRadius: '4px',
                border: '1px solid var(--report-border)',
              }}
            >
              <div>
                <div
                  style={{
                    fontSize: '0.75rem',
                    color: 'var(--report-text-muted)',
                    marginBottom: '0.25rem',
                  }}
                >
                  Current YTD
                </div>
                <div style={{ fontSize: '1.1rem', fontWeight: '600' }}>
                  {formatCurrency(projection.currentYtd)}
                </div>
              </div>
              <div>
                <div
                  style={{
                    fontSize: '0.75rem',
                    color: 'var(--report-text-muted)',
                    marginBottom: '0.25rem',
                  }}
                >
                  Last YTD
                </div>
                <div style={{ fontSize: '1.1rem', fontWeight: '600' }}>
                  {formatCurrency(projection.lastYtd)}
                </div>
              </div>
              {projection.growthPercent !== null && (
                <div>
                  <div
                    style={{
                      fontSize: '0.75rem',
                      color: 'var(--report-text-muted)',
                      marginBottom: '0.25rem',
                    }}
                  >
                    Growth %
                  </div>
                  <div
                    style={{
                      fontSize: '1.1rem',
                      fontWeight: '600',
                      color: growthColor,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.25rem',
                    }}
                  >
                    <span>{growthIndicator}</span>
                    <span>{formatPercentage(projection.growthPercent)}</span>
                  </div>
                </div>
              )}
              {projection.projectedFullYear !== null && (
                <div>
                  <div
                    style={{
                      fontSize: '0.75rem',
                      color: 'var(--report-text-muted)',
                      marginBottom: '0.25rem',
                    }}
                  >
                    Projected Full-Year Sales
                  </div>
                  <div
                    style={{ fontSize: '1.3rem', fontWeight: '700', color: 'var(--report-accent)' }}
                  >
                    {formatCurrency(projection.projectedFullYear)}
                  </div>
                </div>
              )}
            </div>

            {showDetails && (
              <div
                style={{
                  marginTop: '1rem',
                  padding: '1rem',
                  backgroundColor: 'var(--report-surface-subtle)',
                  borderRadius: '4px',
                  fontSize: '0.85rem',
                }}
              >
                <div style={{ fontWeight: '600', marginBottom: '0.75rem', fontSize: '0.9rem' }}>
                  Calculation Formula:
                </div>
                <div
                  style={{
                    backgroundColor: 'var(--report-surface)',
                    padding: '0.75rem',
                    borderRadius: '4px',
                    marginBottom: '1rem',
                    fontFamily: 'monospace',
                    fontSize: '0.8rem',
                    border: '1px solid var(--report-border)',
                  }}
                >
                  <div style={{ marginBottom: '0.5rem' }}>
                    <strong>Growth %</strong> = ((Current YTD - Last YTD) / Last YTD) × 100
                  </div>
                  <div style={{ marginBottom: '0.5rem' }}>
                    = (({formatCurrency(projection.currentYtd)} -{' '}
                    {formatCurrency(projection.lastYtd)}) / {formatCurrency(projection.lastYtd)}) ×
                    100
                  </div>
                  <div style={{ color: growthColor, fontWeight: '600' }}>
                    = {formatPercentage(projection.growthPercent || 0)}
                  </div>
                  <div
                    style={{
                      marginTop: '0.75rem',
                      paddingTop: '0.75rem',
                      borderTop: '1px solid var(--report-border)',
                    }}
                  >
                    <div
                      style={{
                        marginBottom: '0.5rem',
                        fontSize: '0.75rem',
                        color: 'var(--report-text-muted)',
                      }}
                    >
                      <strong>Step 1:</strong> Calculate Seasonality Factor (for remaining period)
                    </div>
                    <div style={{ marginBottom: '0.5rem' }}>
                      <strong>Seasonality Factor</strong> = Last Year Remaining / Last YTD
                    </div>
                    <div style={{ marginBottom: '0.5rem' }}>
                      = {formatCurrency(projection.lastYearRemaining || 0)} /{' '}
                      {formatCurrency(projection.lastYtd)}
                    </div>
                    <div style={{ color: '#28a745', fontWeight: '600', marginBottom: '0.75rem' }}>
                      = {projection.seasonalityFactor?.toFixed(4) || 'N/A'}
                    </div>
                    <div
                      style={{
                        marginBottom: '0.5rem',
                        fontSize: '0.75rem',
                        color: 'var(--report-text-muted)',
                      }}
                    >
                      <strong>Step 2:</strong> Project Remaining Period
                    </div>
                    <div style={{ marginBottom: '0.5rem' }}>
                      <strong>Projected Remaining</strong> = Current YTD × Seasonality Factor
                    </div>
                    <div style={{ marginBottom: '0.5rem' }}>
                      = {formatCurrency(projection.currentYtd)} ×{' '}
                      {projection.seasonalityFactor?.toFixed(4) || 'N/A'}
                    </div>
                    <div style={{ color: '#17a2b8', fontWeight: '600', marginBottom: '0.75rem' }}>
                      = {formatCurrency(projection.projectedRemaining || 0)}
                    </div>
                    <div
                      style={{
                        marginBottom: '0.5rem',
                        fontSize: '0.75rem',
                        color: 'var(--report-text-muted)',
                      }}
                    >
                      <strong>Step 3:</strong> Calculate Full-Year Projection
                    </div>
                    <div style={{ marginBottom: '0.5rem' }}>
                      <strong>Projected Full-Year Sales</strong> = Current YTD + Projected Remaining
                    </div>
                    <div style={{ marginBottom: '0.5rem' }}>
                      = {formatCurrency(projection.currentYtd)} +{' '}
                      {formatCurrency(projection.projectedRemaining || 0)}
                    </div>
                    <div style={{ color: 'var(--report-accent)', fontWeight: '600' }}>
                      = {formatCurrency(projection.projectedFullYear || 0)}
                    </div>
                  </div>
                </div>

                <div style={{ fontWeight: '600', marginBottom: '0.5rem' }}>Source Numbers:</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
                  <div>
                    <strong>Current YTD:</strong> {formatCurrency(projection.currentYtd)}
                  </div>
                  <div>
                    <strong>Last YTD:</strong> {formatCurrency(projection.lastYtd)}
                  </div>
                  <div>
                    <strong>Last Year Remaining:</strong>{' '}
                    {formatCurrency(projection.lastYearRemaining || 0)}
                  </div>
                  <div>
                    <strong>Last Year Full-Year:</strong> {formatCurrency(projection.lastYearSales)}
                  </div>
                  <div>
                    <strong>Seasonality Factor:</strong>{' '}
                    {projection.seasonalityFactor?.toFixed(4) || 'N/A'}
                  </div>
                  <div>
                    <strong>Projected Remaining:</strong>{' '}
                    {formatCurrency(projection.projectedRemaining || 0)}
                  </div>
                </div>
                <div style={{ marginTop: '0.75rem', fontWeight: '600', marginBottom: '0.5rem' }}>
                  Time Period:
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
                  <div>
                    <strong>Days Elapsed:</strong> {projection.daysElapsed} days
                  </div>
                  <div>
                    <strong>Days Remaining:</strong> {projection.daysRemaining} days
                  </div>
                </div>
                <div style={{ marginTop: '0.75rem', fontWeight: '600' }}>Date Ranges:</div>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: '0.5rem',
                    marginTop: '0.25rem',
                  }}
                >
                  <div>
                    <strong>Current YTD:</strong> {projection.dateRanges.currentYtdStart} to{' '}
                    {projection.dateRanges.currentYtdEnd}
                  </div>
                  <div>
                    <strong>Last YTD:</strong> {projection.dateRanges.lastYtdStart} to{' '}
                    {projection.dateRanges.lastYtdEnd}
                  </div>
                  <div>
                    <strong>Last Year Remaining:</strong>{' '}
                    {projection.dateRanges.lastYearRemainingStart} to{' '}
                    {projection.dateRanges.lastYearRemainingEnd}
                  </div>
                  <div>
                    <strong>Last Year Full-Year:</strong> {projection.dateRanges.lastYearStart} to{' '}
                    {projection.dateRanges.lastYearEnd}
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  if (loading && !dashboardData) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--report-text)' }}>
        <div>Loading dashboard data...</div>
      </div>
    );
  }

  if (error && !dashboardData) {
    return (
      <div style={{ padding: '2rem', color: 'var(--report-text)' }}>
        <div style={{ color: '#dc3545', marginBottom: '1rem' }}>Error: {error}</div>
        <button
          onClick={() => loadDashboardData()}
          style={{
            padding: '0.5rem 1rem',
            backgroundColor: 'var(--report-accent)',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          @media (max-width: 768px) {
            /* On mobile, loader should cover full screen below header */
          }
        `}
      </style>
      <ReportHeader
        filters={filters}
        dataFreshness={dashboardData?.dataFreshness}
        title="Executive Dashboard"
        comparePeriod={dashboardData?.kpis?.comparePeriod}
      />
      <FilterBar
        filters={filters}
        onFiltersChange={handleFiltersChange}
        onRun={handleRun}
        onReset={handleReset}
        loading={loading}
        executiveDashboardView={mainTab}
      />
      <div
        style={{
          flex: 1,
          overflow: 'auto',
          padding: '1.5rem',
          backgroundColor: 'var(--report-surface-subtle)',
          position: 'relative',
        }}
      >
        {loading && dashboardData && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: `${sidebarOffset}px`, // Dynamically match main-content margin-left
              right: 0,
              bottom: 0,
              backgroundColor: 'var(--report-overlay)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000,
              transition: 'left 0.3s ease',
            }}
          >
            <div
              style={{
                border: '4px solid var(--report-spinner-track)',
                borderTop: '4px solid var(--report-accent)',
                borderRadius: '50%',
                width: '40px',
                height: '40px',
                animation: 'spin 1s linear infinite',
                marginBottom: '1rem',
              }}
            />
            <div
              style={{ fontSize: '1.1rem', fontWeight: '500', color: 'var(--report-text-muted)' }}
            >
              Loading dashboard data...
            </div>
          </div>
        )}
        {error && dashboardData && (
          <div
            style={{
              marginBottom: '1rem',
              padding: '0.75rem 1rem',
              backgroundColor: 'var(--report-error-bg)',
              border: '1px solid var(--report-error-border)',
              color: 'var(--report-error-text)',
              borderRadius: '4px',
            }}
          >
            {error}
          </div>
        )}
        {drillDownLoading && !drillDownData && (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '3rem',
              color: 'var(--report-text-muted)',
            }}
          >
            <div
              style={{
                border: '4px solid var(--report-spinner-track)',
                borderTop: '4px solid var(--report-accent)',
                borderRadius: '50%',
                width: '40px',
                height: '40px',
                animation: 'spin 1s linear infinite',
                marginBottom: '1rem',
              }}
            />
            <div style={{ fontSize: '1.1rem', fontWeight: '500' }}>Loading drill-down data...</div>
            <style>
              {`
                @keyframes spin {
                  0% { transform: rotate(0deg); }
                  100% { transform: rotate(360deg); }
                }
              `}
            </style>
          </div>
        )}
        {!drillDownLoading && drillDownData ? (
          // Drill-down view
          <div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                marginBottom: '1.5rem',
                gap: '1rem',
              }}
            >
              <button
                onClick={handleBackToMain}
                style={{
                  padding: '0.5rem 1rem',
                  backgroundColor: 'var(--report-text-muted)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '0.9rem',
                }}
              >
                ← Back
              </button>
              <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: '600' }}>
                Drill-Down: {drillDownData.itemName}
              </h2>
            </div>
            {drillDownLoading ? (
              <div
                style={{ textAlign: 'center', padding: '2rem', color: 'var(--report-text-muted)' }}
              >
                Loading drill-down data...
              </div>
            ) : (
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                  gap: '1.5rem',
                }}
              >
                {drillDownData.data.products && drillDownData.data.products.length > 0 && (
                  <TopDeclinersWidget
                    title="Top Declining Products"
                    decliners={drillDownData.data.products}
                    limit={10}
                    exportType="product"
                  />
                )}
                {drillDownData.data.brands && drillDownData.data.brands.length > 0 && (
                  <TopDeclinersWidget
                    title="Top Declining Brands"
                    decliners={drillDownData.data.brands}
                    limit={10}
                    exportType="brand"
                  />
                )}
                {drillDownData.data.categories && drillDownData.data.categories.length > 0 && (
                  <TopDeclinersWidget
                    title="Top Declining Categories"
                    decliners={drillDownData.data.categories}
                    limit={10}
                    exportType="category"
                  />
                )}
                {drillDownData.data.customers && drillDownData.data.customers.length > 0 && (
                  <TopDeclinersWidget
                    title="Top Declining Customers"
                    decliners={drillDownData.data.customers}
                    limit={10}
                    exportType="customer"
                  />
                )}
                {drillDownData.data.salesAgents && drillDownData.data.salesAgents.length > 0 && (
                  <TopDeclinersWidget
                    title="Top Declining Sales Agents"
                    decliners={drillDownData.data.salesAgents}
                    limit={10}
                    exportType="salesAgent"
                  />
                )}
                {drillDownData.data.territories && drillDownData.data.territories.length > 0 && (
                  <TopDeclinersWidget
                    title="Top Declining Territories"
                    decliners={drillDownData.data.territories}
                    limit={10}
                    exportType="territory"
                  />
                )}
                {Object.keys(drillDownData.data).length === 0 && (
                  <div
                    style={{
                      gridColumn: '1 / -1',
                      textAlign: 'center',
                      padding: '2rem',
                      color: 'var(--report-text-muted)',
                      fontStyle: 'italic',
                    }}
                  >
                    No drill-down data available for this item.
                  </div>
                )}
              </div>
            )}
          </div>
        ) : (
          // Main dashboard view
          !drillDownLoading &&
          dashboardData && (
            <>
              {/* KPI Tiles */}
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                  gap: '1rem',
                  marginBottom: '2rem',
                }}
              >
                <KPITile
                  title="Revenue"
                  data={dashboardData.kpis.revenue}
                  formatter={formatCurrency}
                  onExport={() => handleExportKPI('revenue', 'Revenue')}
                />
                <KPITile title="Cases" data={dashboardData.kpis.cases} formatter={formatNumber} />
                <KPITile
                  title="Active Customers"
                  data={dashboardData.kpis.customers}
                  formatter={formatNumber}
                />
                <KPITile
                  title="Total Orders"
                  data={dashboardData.kpis.orders}
                  formatter={formatNumber}
                  onExport={() => handleExportKPI('orders', 'Total Orders')}
                />
              </div>

              {/* Year-end Sales Projection - Only show for YOY comparison */}
              {filters.compareMode !== 'previous' && (
                <div style={{ marginBottom: '2rem' }}>
                  <YearEndProjectionWidget projection={yearEndProjection} />
                </div>
              )}

              <ExecutiveDashboardMainTabs
                activeTab={mainTab}
                onTabChange={handleMainTabChange}
                breakdownContent={
                  <ExecutiveComparisonBreakdownPanel
                    reportFilters={filters}
                    enabled={reportHasBeenRun}
                  />
                }
                moversContent={
                  <ExecutiveMovementInsightsPanel>
                    {reportHasBeenRun ? (
                      <div
                        style={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                          gap: '1.5rem',
                          marginBottom: '2rem',
                        }}
                      >
                        <TopDeclinersWidget
                          title="Top Decliners by Category (Top 10)"
                          decliners={dashboardData.topDecliners.categories}
                          limit={loadedCount.category || 5}
                          drillDownType="category"
                          exportType="category"
                          showRankingToggle={true}
                          sortBy={sortBy.category || 'absolute'}
                          onSortByChange={(newSortBy) => {
                            handleSortByChange('category', newSortBy);
                          }}
                          mode={mode.category || 'decliners'}
                          onModeChange={(newMode) => {
                            handleModeChange('category', newMode);
                          }}
                          showViewMore={true}
                          onViewMore={() => handleLoadMore('category')}
                          typeKey="category"
                          hasMore={
                            dashboardData.topDecliners.categories.length >=
                            (loadedCount.category || 10)
                          }
                        />
                        <TopDeclinersWidget
                          title="Top Decliners by Brand (Top 10)"
                          decliners={dashboardData.topDecliners.brands}
                          limit={loadedCount.brand || 5}
                          drillDownType="brand"
                          exportType="brand"
                          showRankingToggle={true}
                          sortBy={sortBy.brand || 'absolute'}
                          onSortByChange={(newSortBy) => {
                            handleSortByChange('brand', newSortBy);
                          }}
                          mode={mode.brand || 'decliners'}
                          onModeChange={(newMode) => {
                            handleModeChange('brand', newMode);
                          }}
                          showViewMore={true}
                          onViewMore={() => handleLoadMore('brand')}
                          typeKey="brand"
                          hasMore={
                            dashboardData.topDecliners.brands.length >= (loadedCount.brand || 10)
                          }
                        />
                        <TopDeclinersWidget
                          title="Top Decliners by Product (Top 10)"
                          decliners={dashboardData.topDecliners.products}
                          limit={loadedCount.product || 10}
                          drillDownType="product"
                          exportType="product"
                          showRankingToggle={true}
                          sortBy={sortBy.product || 'absolute'}
                          onSortByChange={(newSortBy) => {
                            handleSortByChange('product', newSortBy);
                          }}
                          mode={mode.product || 'decliners'}
                          onModeChange={(newMode) => {
                            handleModeChange('product', newMode);
                          }}
                          showViewMore={true}
                          onViewMore={() => handleLoadMore('product')}
                          typeKey="product"
                          hasMore={
                            dashboardData.topDecliners.products.length >=
                            (loadedCount.product || 10)
                          }
                        />
                        <TopDeclinersWidget
                          title="Top Decliners by Customer (Top 10)"
                          decliners={dashboardData.topDecliners.customers}
                          limit={loadedCount.customer || 10}
                          drillDownType="customer"
                          exportType="customer"
                          showRankingToggle={true}
                          sortBy={sortBy.customer || 'absolute'}
                          onSortByChange={(newSortBy) => {
                            handleSortByChange('customer', newSortBy);
                          }}
                          mode={mode.customer || 'decliners'}
                          onModeChange={(newMode) => {
                            handleModeChange('customer', newMode);
                          }}
                          showViewMore={true}
                          onViewMore={() => handleLoadMore('customer')}
                          typeKey="customer"
                          hasMore={
                            dashboardData.topDecliners.customers.length >=
                            (loadedCount.customer || 10)
                          }
                        />
                        <TopDeclinersWidget
                          title="Top Decliners by Sales Agent (Top 10)"
                          decliners={dashboardData.topDecliners.salesAgents}
                          limit={loadedCount.salesAgent || 10}
                          drillDownType="salesAgent"
                          exportType="salesAgent"
                          showRankingToggle={true}
                          sortBy={sortBy.salesAgent || 'absolute'}
                          onSortByChange={(newSortBy) => {
                            handleSortByChange('salesAgent', newSortBy);
                          }}
                          mode={mode.salesAgent || 'decliners'}
                          onModeChange={(newMode) => {
                            handleModeChange('salesAgent', newMode);
                          }}
                          showViewMore={true}
                          onViewMore={() => handleLoadMore('salesAgent')}
                          typeKey="salesAgent"
                          hasMore={
                            dashboardData.topDecliners.salesAgents.length >=
                            (loadedCount.salesAgent || 10)
                          }
                        />
                        <TopDeclinersWidget
                          title="Top Decliners by Territory (Top 10)"
                          decliners={dashboardData.topDecliners.territories}
                          limit={loadedCount.territory || 10}
                          drillDownType="territory"
                          exportType="territory"
                          showRankingToggle={true}
                          sortBy={sortBy.territory || 'absolute'}
                          onSortByChange={(newSortBy) => {
                            handleSortByChange('territory', newSortBy);
                          }}
                          mode={mode.territory || 'decliners'}
                          onModeChange={(newMode) => {
                            handleModeChange('territory', newMode);
                          }}
                          showViewMore={true}
                          onViewMore={() => handleLoadMore('territory')}
                          typeKey="territory"
                          hasMore={
                            dashboardData.topDecliners.territories.length >=
                            (loadedCount.territory || 10)
                          }
                        />
                      </div>
                    ) : (
                      <div
                        style={{
                          textAlign: 'center',
                          padding: '2.5rem 1rem',
                          color: 'var(--report-text-muted)',
                          fontSize: '0.95rem',
                        }}
                      >
                        Run the report to load decliners and performers.
                      </div>
                    )}
                  </ExecutiveMovementInsightsPanel>
                }
              />
            </>
          )
        )}
      </div>
    </div>
  );
};

export default ExecutiveDashboardPage;
